import { FC, useCallback } from 'react';
import { getFormatedDateTime } from 'components/HoleDetection/TabsContent/HoleDetectionsTable/HoleDetectionsTable';
import { useTranslation } from 'react-i18next';
import { Chip } from '@mui/material';
import { useNavigate } from 'react-router';
import Table from 'atoms/components/Table';
import { ReactComponent as PenIcon } from 'atoms/icons/svg/pen.svg';
import { ReactComponent as ClockIcon } from 'atoms/icons/svg/clock.svg';
import MobileList from '../Mobile';
import { HoleDectionListItem } from 'models/holeDetection';
import { getStatusColorClasses } from '../DeviationList';
import './DeviationListTable.scss';

type Props = {
  holes: HoleDectionListItem[];
};

const DeviationListTable: FC<Props> = ({ holes }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusValue = useCallback((_value, row) => {
    const { acknowledged_by_customer, escape_risk } = row || {};

    return (
      <div
        className={`chip ${getStatusColorClasses(escape_risk, acknowledged_by_customer)}`}
      >
        {acknowledged_by_customer ? t('Confirmed') : t('New')}
      </div>
    );
  }, []);

  const getPenCodeValue = useCallback((penCode) => {
    return (
      <div className="pen-icon">
        <PenIcon />
        {t('Pen')} {penCode}
      </div>
    );
  }, []);

  const getDeviationValue = useCallback((escapeRisk) => {
    return (
      <Chip
        label={
          escapeRisk ? (
            <span className="major-net-break">{t('Major net damage')}</span>
          ) : (
            t('Small net damage')
          )
        }
        className={escapeRisk ? 'risk-colour' : ''}
      />
    );
  }, []);

  const getDescriptionValue = useCallback((_value, row) => {
    const { horizontal_line_breaks, vertical_line_breaks, position, depth } =
      row;

    return (
      <>
        {`${horizontal_line_breaks ?? 0} x ${vertical_line_breaks ?? 0} ${t('masks')}`}
        <br />
        <span className="position-label">{t('Position')}:</span>
        <span className="position">{`${position}° | ${depth}m`}</span>
      </>
    );
  }, []);

  const handleRowClick = (row) => {
    const { image_id } = row ?? {};
    if (image_id) {
      navigate(`/holes/${image_id}`);
    }
  };

  return (
    <Table
      tableProps={{
        className: 'deviation-list-table',
      }}
      columns={[
        {
          label: 'Status',
          key: 'status',
          className: 'status-column',
          render: getStatusValue,
        },
        {
          label: 'Discovered',
          key: 'annotated_at',
          render: (_value) => <>{getFormatedDateTime(_value as number)}</>,
        },
        {
          label: 'Pen',
          key: 'pen_code',
          className: 'pen-cell',
          render: getPenCodeValue,
        },
        {
          label: 'Deviation',
          key: 'escape_risk',
          render: getDeviationValue,
        },
        {
          label: 'Description',
          key: 'description',
          className: 'description-column',
          render: getDescriptionValue,
        },
      ]}
      data={holes}
      onRowClick={handleRowClick}
      mobileBottomColumn={(row) => (
        <div className="timefield--left">
          <ClockIcon />

          <span className="timefield-title">
            {getFormatedDateTime(row?.annotated_at)}
          </span>
        </div>
      )}
      mobileMainContent={(row) => <MobileList row={row} />}
    />
  );
};

export default DeviationListTable;
